import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '0';
  return new Intl.NumberFormat("en-US").format(n);
}

export function formatPrice(price: number): string {
  if (price >= 1_000_000_000) {
    return (price / 1_000_000_000).toFixed(1).replace(".0", "") + " tỷ";
  }
  if (price >= 1_000_000) {
    return (price / 1_000_000).toFixed(0) + " triệu";
  }
  return formatNumber(price);
}

export function formatPriceFull(price: number): string {
  return formatNumber(price) + " đ";
}

export function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return "Vừa xong";
  if (mins < 60) return `${mins} phút trước`;
  if (hours < 24) return `${hours} giờ trước`;
  if (days < 7) return `${days} ngày trước`;
  return formatDate(dateStr);
}

export function truncate(str: string, len: number): string {
  if (str.length <= len) return str;
  return str.slice(0, len).trimEnd() + "…";
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]/g, "");
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .slice(-2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

/* ─── helpers ─── */
export function formatPriceStr(str: string) {
  const n = parseFloat(str);
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} tỷ`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)} triệu`;
  return formatNumber(n);
}

export function formatPriceExact(str: string | number) {
  return formatNumber(str);
}
export function getPrimaryImg(images: any[]): string | null {
  if (!images?.length) return null;
  return (
    (images.find((i) => i.isPrimary) ?? images[0])?.image?.publicUrl ?? null
  );
}

export function fmtSalary(min: string, max: string, unit: string) {
  const fmt = (s: string) => {
    const n = parseFloat(s);
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}tr`;
    return formatNumber(n);
  };
  return `${fmt(min)} – ${fmt(max)} ${unit}`;
}

export function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return "Vừa đăng";
  if (h < 24) return `${h} giờ trước`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} ngày trước`;
  return `${Math.floor(d / 30)} tháng trước`;
}

export function getApiErrorMessage(err: any, fallback: string = 'Đã có lỗi xảy ra'): string {
  const data = err?.response?.data;
  if (!data) {
    return err?.message || fallback;
  }
  if (Array.isArray(data.message) && data.message.length > 0) {
    return data.message.join(' | ');
  }
  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message;
  }
  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error;
  }
  if (typeof data.details === 'string' && data.details.trim()) {
    return data.details;
  }
  if (Array.isArray(data.details) && data.details.length > 0) {
    return data.details.join(' | ');
  }
  return fallback;
}
